<template>
  <div
    ref="rootEl"
    class="plan-card relative flex w-full flex-col rounded-[12px] tablet-h:w-[420px]"
    :class="
      highlight ? 'plan-card-highlight' : 'border border-black/[0.12] bg-white'
    "
  >
    <div class="px-[20px] pt-[20px] pb-[20px] tablet-h:px-[30px]">
      <h3
        class="flex items-center gap-[11px] text-[22px] leading-[34px] font-medium whitespace-nowrap text-gray-800"
      >
        <img
          v-if="highlight"
          :src="MembershipCard"
          alt=""
          class="pointer-events-none h-[20px] w-auto shrink-0 select-none"
        />
        {{ plan.name }}
      </h3>

      <p
        class="mt-[5px] text-[15px] leading-[22px] text-gray-800 tablet-h:h-[44px]"
      >
        {{ tagline }}
      </p>

      <div class="mt-[20px] flex flex-col tablet-h:h-[83px]">
        <p
          v-if="isFree"
          class="text-[36px] leading-[44px] font-medium text-gray-800 tablet-h:mt-auto"
        >
          Free
        </p>

        <template v-else>
          <div class="flex items-baseline gap-[2px]">
            <span class="text-[36px] leading-[40px] font-medium text-gray-800">
              {{ subscriptionPrice?.details?.renderedPrice }}
            </span>
            <span
              v-if="subscriptionSuffix"
              class="text-[19px] leading-[17px] font-medium text-gray-800 italic"
              v-html="renderFine(subscriptionSuffix)"
            />
          </div>

          <div v-if="filePrice" class="mt-[9px] flex items-baseline gap-[5px]">
            <span class="text-[28px] leading-[34px] font-medium text-gray-800">
              +{{ filePrice.details?.renderedPrice }}
            </span>
            <span
              v-if="fileSuffix"
              class="text-[18px] leading-[20px] font-medium text-gray-800 italic"
              v-html="renderFine(fileSuffix)"
            />
          </div>
        </template>
      </div>

      <Button
        class="mt-[20px] w-full"
        :variant="isFree ? 'stroke' : 'primary'"
        size="lg"
        :loading="disabled"
        @click="handleClick"
      >
        {{ buttonCopy }}
      </Button>
    </div>

    <div
      v-if="includeGroups.length"
      class="border-t border-black/[0.08] px-[20px] pt-[20px] pb-[26px] tablet-h:px-[30px]"
    >
      <div
        v-for="(group, groupIndex) in includeGroups"
        :key="groupIndex"
        :class="groupIndex > 0 && 'mt-[19px]'"
      >
        <div v-if="group.heading" class="flex items-center gap-[8px]">
          <p class="text-[15px] leading-[18px] font-medium text-gray-800">
            {{ group.heading.text }}
          </p>
          <Link
            v-if="group.heading.link"
            size="md"
            icon="arrow"
            class="cursor-pointer"
            @click="handleLink(group.heading.link)"
          >
            {{ group.heading.link.text }}
          </Link>
        </div>

        <ul
          v-if="group.items.length"
          :class="['flex flex-col gap-[9px]', group.heading && 'mt-[9px]']"
        >
          <li
            v-for="(item, index) in group.items"
            :key="index"
            class="flex gap-[12px]"
          >
            <CheckmarkIcon
              class="mt-[3px] size-[13px] shrink-0 text-gray-800"
              aria-hidden="true"
            />
            <div class="flex-1">
              <p class="text-[15px] leading-[18px] text-gray-800">
                <component :is="renderText(item.text)" />
              </p>
              <p
                v-if="item.description"
                class="mt-[2px] text-[13px] leading-[20px] text-gray-500"
              >
                {{ item.description }}
              </p>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, h, onBeforeUnmount, onMounted, ref } from 'vue'
import { Button, Link } from '@dacr/library'
import { CheckmarkIcon } from '@dacr/icons'
import {
  PlanItemStatus,
  PlanPriceType,
  type Plan,
  type PlanInclude,
} from '@/shared/graphql/legacy/generated'
import { SelectPlanType } from '@/shared/types/index'
import { useBillingStore } from '@/features/billing/store'
import { renderFine } from '@/features/billing/utils/fine'
import { t } from '@/shared/i18n'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import MembershipCard from '@/assets/images/MembershipCard.png'

const props = defineProps<{
  plan: Plan
  type: SelectPlanType
  disabled?: boolean
}>()

const emit = defineEmits(['selected'])

const billingStore = useBillingStore()

const highlight = computed(() => !!props.plan.headerText)

const displayItem = computed(
  () =>
    props.plan.items.find(
      (item, index) =>
        item.status === PlanItemStatus.Active ||
        index === props.plan.items.length - 1
    ) || props.plan.items[0]
)

const subscriptionPrice = computed(() =>
  displayItem.value?.prices.find(
    (price) => price.type === PlanPriceType.Subscription
  )
)

const filePrice = computed(() =>
  displayItem.value?.prices.find((price) => price.type === PlanPriceType.File)
)

const isFree = computed(
  () =>
    !subscriptionPrice.value?.details?.amount &&
    !filePrice.value?.details?.amount
)

// Plan copy arrives from the gateway as translation keys — resolve it before it
// reaches the template, and before renderText/renderFine, which match on the
// literal 'Dacr AI' and '**…**' that the backend validator guarantees survive
// translation.
const tagline = computed(() => t(props.plan.description))

const subscriptionSuffix = computed(() =>
  t(subscriptionPrice.value?.details?.suffix)
)

const fileSuffix = computed(() => t(filePrice.value?.details?.suffix))

interface IncludeGroup {
  heading?: PlanInclude
  items: PlanInclude[]
}

const includeGroups = computed<IncludeGroup[]>(() => {
  const source: PlanInclude[] = props.plan.includes?.length
    ? props.plan.includes
    : (props.plan.benefits || []).map((text) => ({
        __typename: 'PlanInclude' as const,
        text,
      }))

  const includes: PlanInclude[] = source.map((include) => ({
    ...include,
    text: t(include.text),
    description: t(include.description),
    link: include.link
      ? { ...include.link, text: t(include.link.text) }
      : include.link,
  }))

  return includes.reduce<IncludeGroup[]>((groups, include) => {
    if (include.separator) {
      groups.push({ heading: include, items: [] })
    } else {
      if (!groups.length) groups.push({ items: [] })
      groups[groups.length - 1].items.push(include)
    }
    return groups
  }, [])
})

const isSubscribedToPlan = computed(() =>
  props.plan.items.some(
    (item) => billingStore.billing?.plan.id === item.productId
  )
)

const isSubscribedToBundlePlan = computed(
  () => isSubscribedToPlan.value && props.plan.items.length > 1
)

const buttonCopy = computed(() => {
  if (props.type === SelectPlanType.Change && isSubscribedToBundlePlan.value) {
    return 'Upgrade limit'
  }
  if (props.type === SelectPlanType.Change && isSubscribedToPlan.value) {
    return 'See account details'
  }
  if (props.type === SelectPlanType.Change && !isSubscribedToPlan.value) {
    return 'Switch plan'
  }
  return 'Get started'
})

const handleClick = () => {
  emit('selected', props.plan, displayItem.value)
}

const handleLink = (link: { text: string; url: string }) => {
  if (/^https?:\/\//.test(link.url)) {
    window.open(link.url, '_blank', 'noopener')
    return
  }
  openDialog(Dialog.ProMembershipDetails)
}

const renderText = (text: string) => {
  if (!text.includes('Dacr AI')) return h('span', text)
  const parts = text.split('Dacr AI')
  const children: (string | ReturnType<typeof h>)[] = []
  parts.forEach((part, index) => {
    if (index > 0) {
      children.push(
        h(
          'span',
          {
            class:
              'bg-linear-to-r from-yellow-500 via-[#F84600] to-[#CE489A] font-medium text-transparent',
            style: '-webkit-background-clip:text;background-clip:text',
          },
          'Dacr AI'
        )
      )
    }
    if (part) children.push(part)
  })
  return h('span', children)
}

const rootEl = ref<HTMLElement>()
let raf = 0
let current = 135
let target = 135

const tick = () => {
  const delta = target - current
  if (Math.abs(delta) < 0.05) {
    current = target
    raf = 0
  } else {
    current += delta * 0.08
    raf = requestAnimationFrame(tick)
  }
  rootEl.value?.style.setProperty('--plan-angle', `${current}deg`)
}

const onMove = (event: PointerEvent) => {
  const el = rootEl.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  const cx = rect.left + rect.width / 2
  const cy = rect.top + rect.height / 2
  let raw =
    (Math.atan2(event.clientY - cy, event.clientX - cx) * 180) / Math.PI + 138
  while (raw - current > 180) raw -= 360
  while (raw - current < -180) raw += 360
  target = raw
  if (!raf) raf = requestAnimationFrame(tick)
}

onMounted(() => {
  if (highlight.value)
    window.addEventListener('pointermove', onMove, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('pointermove', onMove)
  if (raf) cancelAnimationFrame(raf)
})
</script>

<style>
@property --plan-angle {
  syntax: '<angle>';
  inherits: false;
  initial-value: 135deg;
}

.plan-card-highlight {
  --plan-angle: 135deg;
  background:
    linear-gradient(#fff, #fff) padding-box,
    conic-gradient(
        from var(--plan-angle) at 50% 50%,
        rgba(0, 0, 0, 0.12) 30deg,
        rgba(0, 0, 0, 0.12) 236deg,
        #60ccd6 272deg,
        #60ccd6 352deg,
        rgba(0, 0, 0, 0.12) 360deg
      )
      border-box;
  border: 1px solid transparent;
}
</style>
